import { useEffect, useRef } from 'react';
import type { FieldPath, FieldValues } from 'react-hook-form';
import { useController, useFormContext } from 'react-hook-form';
import { Box } from '@mui/material';
import { MATERIAL_KIND_LABELS, MaterialKind } from '../../enum/materialKind';
import { OptionalPresenceField } from './OptionalPresenceField';
import { RhfMaterialSelect } from './RhfMaterialSelect';

type OptionalMaterialFieldProps<T extends FieldValues> = {
  withName: FieldPath<T>;
  materialName: FieldPath<T>;
  kind: MaterialKind;
};

/**
 * Optional material presence: Without by default; With expands a material
 * select and focuses it for immediate choice.
 */
export function OptionalMaterialField<T extends FieldValues>({
  withName,
  materialName,
  kind,
}: OptionalMaterialFieldProps<T>) {
  const { control, setValue } = useFormContext<T>();
  const { field: withField } = useController({ control, name: withName });
  const enabled = Boolean(withField.value);
  const materialInputRef = useRef<HTMLInputElement>(null);
  const wasEnabledRef = useRef(enabled);
  const kindLabel = MATERIAL_KIND_LABELS[kind].singular;

  useEffect(() => {
    const wasEnabled = wasEnabledRef.current;
    wasEnabledRef.current = enabled;
    if (!wasEnabled && enabled) {
      // Wait for the expand so the Autocomplete popper anchors to final width.
      const id = window.setTimeout(() => {
        const input = materialInputRef.current;
        input?.focus({ preventScroll: true });
        if (input) {
          input.scrollLeft = 0;
        }
      }, 300);
      return () => window.clearTimeout(id);
    }
  }, [enabled]);

  return (
    <OptionalPresenceField
      presenceLabel={kindLabel}
      enabled={enabled}
      onEnabledChange={(next) => {
        withField.onChange(next);
        if (!next) {
          setValue(materialName, '' as never, { shouldDirty: true });
        }
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          minWidth: 0,
          overflow: 'hidden',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <RhfMaterialSelect<T>
          name={materialName}
          label=""
          kind={kind}
          openOnFocus
          disableClearable
          inputRef={materialInputRef}
          sx={{
            width: '100%',
            minWidth: 0,
            maxWidth: '100%',
            height: '100%',
            '& .MuiInputLabel-root': { display: 'none' },
            '& .MuiFormHelperText-root': { display: 'none' },
            '& .MuiAutocomplete-root': { height: '100%' },
            '& .MuiAutocomplete-inputRoot': {
              flexWrap: 'nowrap',
              overflow: 'hidden',
            },
            '& .MuiOutlinedInput-root': {
              height: '100%',
              borderRadius: 0,
              '& fieldset': { border: 'none' },
              '&:hover fieldset': { border: 'none' },
              '&.Mui-focused fieldset': { border: 'none' },
            },
            // Autocomplete sizes the input from the value. A row that opens
            // from collapsed would otherwise keep that narrower measurement.
            '& .MuiAutocomplete-input': {
              width: '100% !important',
              minWidth: '0 !important',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          }}
        />
      </Box>
    </OptionalPresenceField>
  );
}
